import { isValidPassword } from '@/auth';
import { MIN_HOURS_NOTICE_WITHOUT_ER, validateDatesMinNotice } from '@/consultation';
import { hasOnlyLetters } from '@/utils/reducer';

import { FormValues, SchedulingFormValues } from './types';
import type { MissingField } from './utils/profileCompleteness';

const MIN_DESCRIPTION_LENGTH = 100;

// Validation for scheduling step (step 0)
export const validateSchedulingForm = (
  values: Partial<SchedulingFormValues>
): Partial<Record<keyof SchedulingFormValues, string | Array<string | undefined>>> => {
  const errors: Partial<Record<keyof SchedulingFormValues, string | Array<string | undefined>>> =
    {};

  if (!values.description?.trim()) {
    errors.description = 'Required';
  } else if (values.description.trim().length < MIN_DESCRIPTION_LENGTH) {
    errors.description = `Please include at least ${MIN_DESCRIPTION_LENGTH} characters`;
  }

  if (values.dates) {
    const dateErrors = validateDatesMinNotice(values.dates, MIN_HOURS_NOTICE_WITHOUT_ER);
    if (dateErrors.some(Boolean)) {
      errors.dates = dateErrors;
    }
  }

  return errors;
};

// Options for conditional validation
interface ValidateFormOptions {
  isExistingUser?: boolean;
  missingFields?: MissingField[];
}

// Validation for user details form (step 1)
// For existing users with incomplete profiles, only validates the missing fields
export const validateForm = (
  values: Partial<FormValues>,
  options: ValidateFormOptions = {}
): Partial<FormValues> => {
  const errors: Partial<FormValues> = {};
  const { isExistingUser = false, missingFields = [] } = options;

  // Helper to determine if a field should be validated
  // Validate all fields for new users, only missing fields for existing users
  const shouldValidate = (field: MissingField) => {
    return !isExistingUser || missingFields.includes(field);
  };

  // First name validation
  if (shouldValidate('firstName')) {
    if (!values.firstName?.trim()) {
      errors.firstName = 'Required';
    } else if (!hasOnlyLetters(values.firstName)) {
      errors.firstName = 'Only letters allowed';
    }
  }

  // Last name validation
  if (shouldValidate('lastName')) {
    if (!values.lastName?.trim()) {
      errors.lastName = 'Required';
    } else if (!hasOnlyLetters(values.lastName)) {
      errors.lastName = 'Only letters allowed';
    }
  }

  // Company validation
  if (shouldValidate('company')) {
    if (!values.company?.trim()) {
      errors.company = 'Required';
    }
  }

  // Email and password validation - only for new users
  if (!isExistingUser) {
    if (!values.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }

    if (!values.password) {
      errors.password = 'Required';
    } else if (!isValidPassword(values.password)) {
      errors.password = 'Password does not meet requirements';
    }

    if (!values.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (values.password && values.confirmPassword !== values.password) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!values.acceptPrivacyAndTerms) {
      errors.acceptPrivacyAndTerms = 'Required' as unknown as boolean;
    }
  }

  return errors;
};
